/* src/pages/Profile.tsx - PERFIL DE USUARIO */
/* ========================================= */

import React, { useState } from 'react';
import { User, Mail, Phone, Building,  ShoppingBag, GraduationCap, Edit, Save, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { formatPrice } from '../lib/utils';

const Profile: React.FC = () => {
  const { user, logout } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    company: user?.company || ''
  });

  if (!user) {
    return null; // Esto no debería pasar por el ProtectedRoute
  }

  const handleEdit = () => {
    setIsEditing(true);
    setEditedData({
      name: user.name,
      phone: user.phone || '',
      company: user.company || ''
    });
  };

  const handleSave = () => {
    // Aquí iría la lógica para actualizar el perfil
    console.log('Guardando datos:', editedData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedData({
      name: user.name,
      phone: user.phone || '',
      company: user.company || ''
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedData({
      ...editedData,
      [e.target.name]: e.target.value
    });
  };

  // Datos mock para demostración
  const mockOrders = [
    {
      id: 'ORD-001',
      date: '2024-01-15',
      total: 1250,
      status: 'delivered' as const,
      items: [
        { name: 'Panel Solar 400W', quantity: 2, price: 420 },
        { name: 'Inversor 3kW', quantity: 1, price: 410 }
      ]
    },
    {
      id: 'ORD-002',
      date: '2024-01-20',
      total: 890,
      status: 'processing' as const,
      items: [
        { name: 'Batería Litio 100Ah', quantity: 1, price: 890 }
      ]
    }
  ];

  const mockCourses = [
    {
      id: 'solar-basico',
      title: 'Instalación de Sistemas Solares - Básico',
      enrolledDate: '2024-01-10',
      progress: 75,
      status: 'in-progress' as const
    },
    {
      id: 'electricidad-domotica',
      title: 'Domótica e Inmótica Residencial',
      enrolledDate: '2023-12-15',
      progress: 100,
      status: 'completed' as const
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'processing':
      case 'in-progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'pending':
        return 'bg-gray-100 text-gray-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'Entregado';
      case 'processing':
        return 'Procesando';
      case 'pending':
        return 'Pendiente';
      case 'cancelled':
        return 'Cancelado';
      case 'completed':
        return 'Completado';
      case 'in-progress':
        return 'En progreso';
      default:
        return status;
    }
  };

  return (
    <div className="min-h-screen bg-infinity-black">
      {/* Header */}
      <div className="bg-infinity-gradient text-infinity-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center space-x-6">
            <div className="w-24 h-24 bg-infinity-yellow bg-opacity-20 rounded-full flex items-center justify-center">
              <User size={48} className="text-infinity-yellow" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-infinity-yellow">{user.name}</h1>
              <p className="text-xl text-infinity-white/80">{user.email}</p>
              {user.company && (
                <p className="text-infinity-white/60">{user.company}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Información Personal */}
          <div className="lg:col-span-1">
            <div className="bg-infinity-dark rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-infinity-yellow">Información Personal</h2>
                {!isEditing ? (
                  <button
                    onClick={handleEdit}
                    className="p-2 text-infinity-white/60 hover:text-infinity-yellow"
                  >
                    <Edit size={20} />
                  </button>
                ) : (
                  <div className="flex space-x-2">
                    <button
                      onClick={handleSave}
                      className="p-2 text-green-600 hover:text-green-800"
                    >
                      <Save size={20} />
                    </button>
                    <button
                      onClick={handleCancel}
                      className="p-2 text-red-600 hover:text-red-800"
                    >
                      <X size={20} />
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-infinity-white/80 mb-1">
                    Nombre
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="name"
                      value={editedData.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-infinity-yellow rounded-lg focus:ring-2 focus:ring-infinity-yellow focus:border-transparent bg-infinity-black text-infinity-white"
                    />
                  ) : (
                    <div className="flex items-center space-x-2">
                      <User size={16} className="text-infinity-white/60" />
                      <span>{user.name}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-infinity-white/80 mb-1">
                    Email
                  </label>
                  <div className="flex items-center space-x-2">
                    <Mail size={16} className="text-infinity-white/60" />
                    <span>{user.email}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-infinity-white/80 mb-1">
                    Teléfono
                  </label>
                  {isEditing ? (
                    <input
                      type="tel"
                      name="phone"
                      value={editedData.phone}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-infinity-yellow rounded-lg focus:ring-2 focus:ring-infinity-yellow focus:border-transparent bg-infinity-black text-infinity-white"
                    />
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Phone size={16} className="text-infinity-white/60" />
                      <span>{user.phone || 'No especificado'}</span>
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-infinity-white/80 mb-1">
                    Empresa
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="company"
                      value={editedData.company}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-infinity-yellow rounded-lg focus:ring-2 focus:ring-infinity-yellow focus:border-transparent bg-infinity-black text-infinity-white"
                    />
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Building size={16} className="text-infinity-white/60" />
                      <span>{user.company || 'No especificado'}</span>
                    </div>
                  )}
                </div>
              </div>

              <hr className="my-6" />

              <button
                onClick={logout}
                className="w-full text-red-600 hover:text-red-800 font-medium py-2 bg-infinity-yellow/10 rounded mt-6"
              >
                Cerrar Sesión
              </button>
            </div>
          </div>

          {/* Historial y Actividad */}
          <div className="lg:col-span-2 space-y-8">
            {/* Pedidos */}
            <div className="bg-infinity-dark rounded-lg shadow-lg p-6">
              <div className="flex items-center space-x-3 mb-6">
                <ShoppingBag size={24} className="text-infinity-yellow" />
                <h2 className="text-xl font-bold text-infinity-yellow">Mis Pedidos</h2>
              </div>

              {mockOrders.length > 0 ? (
                <div className="space-y-4">
                  {mockOrders.map((order) => (
                    <div key={order.id} className="border border-infinity-yellow/30 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="font-semibold text-infinity-yellow">Pedido #{order.id}</p>
                          <p className="text-sm text-infinity-white/60">{new Date(order.date).toLocaleDateString('es-PE')}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-infinity-yellow">{formatPrice(order.total)}</p>
                          <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(order.status)}`}>
                            {getStatusText(order.status)}
                          </span>
                        </div>
                      </div>
                      <div className="text-sm text-infinity-white/80">
                        {order.items.map((item, index) => (
                          <div key={index} className="flex justify-between">
                            <span>{item.name} x{item.quantity}</span>
                            <span>{formatPrice(item.price * item.quantity)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-infinity-white/60 text-center py-8">No tienes pedidos aún</p>
              )}
            </div>

            {/* Capacitaciones */}
            <div className="bg-infinity-dark rounded-lg shadow-lg p-6">
              <div className="flex items-center space-x-3 mb-6">
                <GraduationCap size={24} className="text-infinity-yellow" />
                <h2 className="text-xl font-bold text-infinity-yellow">Mis Capacitaciones</h2>
              </div>

              {mockCourses.length > 0 ? (
                <div className="space-y-4">
                  {mockCourses.map((course) => (
                    <div key={course.id} className="border border-infinity-yellow/30 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="font-semibold text-infinity-yellow">{course.title}</h3>
                          <p className="text-sm text-infinity-white/60">
                            Inscrito: {new Date(course.enrolledDate).toLocaleDateString('es-PE')}
                          </p>
                        </div>
                        <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(course.status)}`}>
                          {getStatusText(course.status)}
                        </span>
                      </div>
                      <div className="mb-2">
                        <div className="flex justify-between text-sm mb-1">
                          <span>Progreso</span>
                          <span>{course.progress}%</span>
                        </div>
                        <div className="w-full bg-infinity-yellow/20 rounded-full h-2">
                          <div
                            className="bg-infinity-yellow h-2 rounded-full transition-all duration-300"
                            style={{ width: `${course.progress}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-infinity-white/60 text-center py-8">No estás inscrito en ninguna capacitación</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
